import { useCallback, useEffect, useMemo, useState } from "react";
import { PROPHET_FAMILIES, INITIAL_FAMILY_ID } from "@/data/prophetFamiliesData";
import { FamilySelector } from "./FamilySelector";
import { FamilyMediaFrame } from "./FamilyMediaFrame";
import { FamilyProfile } from "./FamilyProfile";

/**
 * FamilyExplorer — right column of the Lore section.
 *
 * Composes: selector (tab rail) + square media frame + profile panel.
 * State is a single `activeId` string. All families come from the shared
 * PROPHET_FAMILIES data array; adding an 11th family only requires a new
 * entry in that array — no code changes here.
 *
 * `onActiveFamilyChange` (optional) fires with the active family object
 * on every selection change (and on initial mount) so parent UI (e.g.
 * the panel-level Reveal Your Prophecy CTA in ProphetsInsight) can
 * adapt its accent color to match the currently selected family.
 */
export function FamilyExplorer({ onActiveFamilyChange } = {}) {
  const [activeId, setActiveId] = useState(INITIAL_FAMILY_ID);

  const activeIndex = useMemo(
    () => PROPHET_FAMILIES.findIndex((f) => f.id === activeId),
    [activeId],
  );
  const active = PROPHET_FAMILIES[activeIndex] || PROPHET_FAMILIES[0];

  useEffect(() => {
    if (typeof onActiveFamilyChange === "function") {
      onActiveFamilyChange(active);
    }
  }, [active, onActiveFamilyChange]);

  const go = useCallback(
    (delta) => {
      const len = PROPHET_FAMILIES.length;
      const next = PROPHET_FAMILIES[((activeIndex + delta) % len + len) % len];
      setActiveId(next.id);
    },
    [activeIndex],
  );

  return (
    <div
      data-testid="family-explorer"
      className="prophets-lore-explorer"
      style={{
        "--family-accent": active.accent,
        "--family-accent-2": active.secondaryAccent || active.accent,
        "--family-accent-3": active.tertiaryAccent || active.accent,
      }}
    >
      <header className="prophets-lore-explorer-head">
        <div>
          <h2
            data-testid="family-explorer-title"
            className="p-nav prophets-lore-explorer-title"
          >
            Discover the Families
          </h2>
        </div>
      </header>

      <div className="prophets-lore-explorer-body">
        <FamilySelector
          families={PROPHET_FAMILIES}
          activeId={activeId}
          onSelect={setActiveId}
        />

        <div className="prophets-lore-explorer-content">
          <FamilyMediaFrame family={active} />
          <FamilyProfile
            family={active}
            total={PROPHET_FAMILIES.length}
            onPrev={() => go(-1)}
            onNext={() => go(+1)}
          />
        </div>
      </div>
    </div>
  );
}
